import type { FC } from 'react'
import { useState } from 'react'

import { Dropzone, MessagePopup, UploadedFileZone } from '@skytech/ui'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'

import documentResources from '@/api/document'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { Translation } from '@/components/i18n/Translation'
import { DOCUMENT_TYPES } from '@/components/util/dropzone/constants'
import useAuthFilesDropzone from '@/components/util/dropzone/useAuthFilesDropzone'
import Warning from '@/components/util/feedback/Warning'
import {
  fetchCustomer,
  getCustomerApplicationFileId,
  getCustomerApplicationFileName,
  getCustomerStatus,
  resetCustomerApplicationFileId,
} from '@/ducks/customerReducer'
import { CUSTOMER_STATUS } from '@/util/customers'

import './application-form.scss'

const ApplicationForm: FC<{ customerId: number }> = ({ customerId }) => {
  const { whiteLabel } = useAppContext()
  const dispatch = useDispatch()
  const [uploadError, setUploadError] = useState('')
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)

  const fileId = useSelector(getCustomerApplicationFileId)
  const fileName = useSelector(getCustomerApplicationFileName)
  const status = useSelector(getCustomerStatus)

  const { authorizeUpload, isAuthorizingUpload } = useAuthFilesDropzone({
    type: DOCUMENT_TYPES.NEW_CUSTOMER_FORM,
    customerId,
    fileTypes: ['pdf'],
    onUpload: () => {
      setUploadError('')
      // @ts-ignore
      dispatch(fetchCustomer(customerId))
    },
    onError(err) {
      setUploadError(err)
    },
  })

  const { refetch: authorizeFileDownload, isInitialLoading: isDownloading } = useQuery(
    queries.documentDownloadAuthorization(fileId),
    () => documentResources.authorizeFileDownload(fileId),
    {
      enabled: false,
      onError() {
        setIsErrorPopupOpen(true)
      },
    }
  )

  if (status !== CUSTOMER_STATUS.FOR_APPROVAL) return null

  return (
    <>
      <Warning className="application-form">
        <h3>
          <Translation id="Complete customer registration" />
        </h3>

        <p>
          <Translation
            id={
              fileId
                ? 'Please wait while we finish setting up this customer.'
                : 'To complete the new customer-process, please download and fill out the following form.'
            }
          />
        </p>

        {fileId ? (
          <UploadedFileZone
            documentFilename={fileName}
            isDownloading={isDownloading}
            onDownloadClick={authorizeFileDownload}
            onRemoveClick={() => dispatch(resetCustomerApplicationFileId())}
          />
        ) : (
          <Dropzone
            id="application-form-dropzone"
            activeDragText={<Translation id="Release file here to upload" />}
            inActiveDragText={<Translation id="Drag file here or click to browse" />}
            customError={uploadError}
            isUploading={isAuthorizingUpload}
            onDrop={authorizeUpload}
            whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
          />
        )}
      </Warning>

      <MessagePopup
        title={<Translation id="File download error" />}
        message={
          <Translation id="We encountered a problem while downloading your file. Please refresh and try again." />
        }
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      />
    </>
  )
}

export default ApplicationForm
